'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';
import { startOfMonth, parse } from 'date-fns';

type ApexCalendarFiltersContextProps = {
  nights: number;
  mealPlans: number[];
  flightTimesOutbound: number[];
  flightTimesInbound: number[];
  currentMonth: Date;
  setNights: (n: number) => void;
  setMealPlans: (m: number[]) => void;
  setFlightTimesOutbound: (v: number[]) => void;
  setFlightTimesInbound: (v: number[]) => void;
  setCurrentMonth: (d: Date) => void;
};

const ApexCalendarFiltersContext = createContext<ApexCalendarFiltersContextProps | undefined>(undefined);

export function ApexCalendarFiltersProvider({
  children,
  initialNights,
  initialCheckin,
}: {
  children: React.ReactNode;
  initialNights: number;
  initialCheckin: string;
}) {
  const [nights, setNights] = useState(initialNights);
  const [mealPlans, setMealPlans] = useState<number[]>([]);
  const [flightTimesOutbound, setFlightTimesOutbound] = useState<number[]>([]);
  const [flightTimesInbound, setFlightTimesInbound] = useState<number[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    startOfMonth(parse(initialCheckin, 'yyyyMMdd', new Date()))
  );

  const value = useMemo(
    () => ({
      nights,
      mealPlans,
      flightTimesOutbound,
      flightTimesInbound,
      currentMonth,
      setNights,
      setMealPlans,
      setFlightTimesOutbound,
      setFlightTimesInbound,
      setCurrentMonth,
    }),
    [nights, mealPlans, flightTimesOutbound, flightTimesInbound, currentMonth]
  );

  return <ApexCalendarFiltersContext.Provider value={value}>{children}</ApexCalendarFiltersContext.Provider>;
}

export function useApexCalendarFilters(): ApexCalendarFiltersContextProps {
  const context = useContext(ApexCalendarFiltersContext);
  if (!context) throw new Error('useApexCalendarFilters must be used within ApexCalendarFiltersProvider');
  return context;
}
